/**
 * LightRenderer — every WebGL2 object of the LightLayer and the five draws of a frame.
 *
 *   prerender(): glow capsules → quarter-res RGBA8 (MAX)          1 instanced draw per lit range
 *                line capsules → full-res MRT RGBA8 + R8 (MAX)     1 instanced draw per lit range
 *   render():    glow composite (screen × gain)                   1 fullscreen triangle
 *                line composite (core over shade, premultiplied)  1 fullscreen triangle
 *                comet sprites (premultiplied)                    1 instanced draw
 *
 * The capsule passes draw only the instance ranges of lit paths (pack.ts instanceRanges;
 * contiguous paths merge into one range), not all ~5 600 segments. All passes are scissored
 * to the screen rectangle of the lit paths. Every piece of GL state touched is restored
 * after each hook (see "GL state snapshot" below for which values are queried and which
 * are reset to MapLibre's documented values).
 *
 * The constructor throws LightGLError when a shader fails to compile/link; prerender()
 * throws it when an offscreen framebuffer is incomplete (targets are freed first). The
 * layer (index.ts) catches both and falls back to MapLibre line layers.
 */
import { INSTANCE_BYTES } from "./pack";
import { BRIDGE_MAX_PX, GLOW_SIGMA_MIN_PX, GLOW_SIGMA_PER_WIDTH, SHADERS } from "./shaders";
import { STATE_FLOATS, STATE_TEXELS } from "./state";

export class LightGLError extends Error {}

/** Comet instance: x, y (origin-relative Mercator), core diameter px, halo diameter px, alpha, scale. */
export const COMET_FLOATS = 6;

export interface FrameParams {
  matrix: Float32Array; //   origin-relative Mercator → clip
  width: number; //          drawing buffer, device px
  height: number;
  dpr: number;
  near: number; //           min clip w
  trailM: number; //         480 CSS px in metres
  shadePx: number; //        0 = no shade
  shade: [number, number, number, number];
  glowGain: number;
  glow: boolean; //          run the glow passes (governor + any glowing path)
  colors: Float32Array; //   5 × vec3: hero bloom preview site ring
  trail: [number, number, number];
  comet: [number, number, number];
  halo: [number, number, number];
  glowColor: [number, number, number];
  scissor: [number, number, number, number] | null; // x, y, w, h (GL origin, device px); null = full frame
  comets: number;
  /** Instance ranges [first, count, …] of the lit paths (line pass) and of the lit paths with glow (glow pass). */
  lineRanges: readonly number[];
  glowRanges: readonly number[];
}

type Uniforms = Record<string, WebGLUniformLocation | null>;
interface Prog {
  p: WebGLProgram;
  u: Uniforms;
}

function compile(gl: WebGL2RenderingContext, type: number, src: string, label: string): WebGLShader {
  const s = gl.createShader(type);
  if (!s) throw new LightGLError(`${label}: createShader failed`);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(s) ?? "";
    gl.deleteShader(s);
    throw new LightGLError(`${label}: ${log.trim() || "compile failed"}`);
  }
  return s;
}

function program(gl: WebGL2RenderingContext, vs: string, fs: string, label: string, names: string[]): Prog {
  const v = compile(gl, gl.VERTEX_SHADER, vs, `${label}.vs`);
  const f = compile(gl, gl.FRAGMENT_SHADER, fs, `${label}.fs`);
  const p = gl.createProgram();
  if (!p) throw new LightGLError(`${label}: createProgram failed`);
  gl.attachShader(p, v);
  gl.attachShader(p, f);
  gl.linkProgram(p);
  gl.deleteShader(v);
  gl.deleteShader(f);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(p) ?? "";
    gl.deleteProgram(p);
    throw new LightGLError(`${label}: ${log.trim() || "link failed"}`);
  }
  const u: Uniforms = {};
  for (const n of names) u[n] = gl.getUniformLocation(p, n);
  return { p, u };
}

const CAPSULE_UNIFORMS = ["u_matrix", "u_viewport", "u_dpr", "u_near", "u_shadePx", "u_glowSigma", "u_glowMin", "u_state", "u_trailM"];

/* ─── GL state snapshot ─────────────────────────────────────────────────── */

/*
 * Two kinds of state are restored after every hook:
 *  - QUERIED (client-cached in Chrome, ~1 µs each): framebuffer bindings, viewport,
 *    program, VAO, array/unpack buffers, active texture + 2D bindings on units 0–2,
 *    BLEND/DEPTH/STENCIL/SCISSOR/CULL enables, unpack alignment/flip/premultiply.
 *  - RESET TO KNOWN VALUES (querying them is a synchronous GPU round trip, measured
 *    0.2–0.4 ms each): blend func (ONE, ONE_MINUS_SRC_ALPHA) and equation FUNC_ADD —
 *    what MapLibre hands custom layers in both hooks (ColorMode.alphaBlended +
 *    setBaseState) — colour mask all on, clear colour transparent, scissor box = frame.
 *    MapLibre also marks all of these dirty after a custom layer, so its cache re-applies.
 * Depth mask and stencil state are never touched.
 */
interface Snap {
  drawFb: WebGLFramebuffer | null;
  readFb: WebGLFramebuffer | null;
  viewport: Int32Array;
  program: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  arrayBuf: WebGLBuffer | null;
  unpackBuf: WebGLBuffer | null;
  activeTex: number;
  tex: (WebGLTexture | null)[];
  blend: boolean;
  depth: boolean;
  stencil: boolean;
  scissor: boolean;
  cull: boolean;
  unpackAlign: number;
  unpackFlip: boolean;
  unpackPremul: boolean;
}

const TEX_UNITS = 3;

function snapshot(gl: WebGL2RenderingContext): Snap {
  const activeTex = gl.getParameter(gl.ACTIVE_TEXTURE) as number;
  const tex: (WebGLTexture | null)[] = [];
  for (let i = 0; i < TEX_UNITS; i++) {
    gl.activeTexture(gl.TEXTURE0 + i);
    tex.push(gl.getParameter(gl.TEXTURE_BINDING_2D) as WebGLTexture | null);
  }
  gl.activeTexture(activeTex);
  return {
    drawFb: gl.getParameter(gl.DRAW_FRAMEBUFFER_BINDING) as WebGLFramebuffer | null,
    readFb: gl.getParameter(gl.READ_FRAMEBUFFER_BINDING) as WebGLFramebuffer | null,
    viewport: gl.getParameter(gl.VIEWPORT) as Int32Array,
    program: gl.getParameter(gl.CURRENT_PROGRAM) as WebGLProgram | null,
    vao: gl.getParameter(gl.VERTEX_ARRAY_BINDING) as WebGLVertexArrayObject | null,
    arrayBuf: gl.getParameter(gl.ARRAY_BUFFER_BINDING) as WebGLBuffer | null,
    unpackBuf: gl.getParameter(gl.PIXEL_UNPACK_BUFFER_BINDING) as WebGLBuffer | null,
    activeTex,
    tex,
    blend: gl.isEnabled(gl.BLEND),
    depth: gl.isEnabled(gl.DEPTH_TEST),
    stencil: gl.isEnabled(gl.STENCIL_TEST),
    scissor: gl.isEnabled(gl.SCISSOR_TEST),
    cull: gl.isEnabled(gl.CULL_FACE),
    unpackAlign: gl.getParameter(gl.UNPACK_ALIGNMENT) as number,
    unpackFlip: gl.getParameter(gl.UNPACK_FLIP_Y_WEBGL) as boolean,
    unpackPremul: gl.getParameter(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL) as boolean,
  };
}

const setCap = (gl: WebGL2RenderingContext, cap: number, on: boolean) => (on ? gl.enable(cap) : gl.disable(cap));

function restore(gl: WebGL2RenderingContext, s: Snap): void {
  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, s.drawFb);
  gl.bindFramebuffer(gl.READ_FRAMEBUFFER, s.readFb);
  gl.viewport(s.viewport[0], s.viewport[1], s.viewport[2], s.viewport[3]);
  gl.useProgram(s.program);
  gl.bindVertexArray(s.vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, s.arrayBuf);
  gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, s.unpackBuf);
  for (let i = 0; i < TEX_UNITS; i++) {
    gl.activeTexture(gl.TEXTURE0 + i);
    gl.bindTexture(gl.TEXTURE_2D, s.tex[i]);
  }
  gl.activeTexture(s.activeTex);
  setCap(gl, gl.BLEND, s.blend);
  setCap(gl, gl.DEPTH_TEST, s.depth);
  setCap(gl, gl.STENCIL_TEST, s.stencil);
  setCap(gl, gl.SCISSOR_TEST, s.scissor);
  setCap(gl, gl.CULL_FACE, s.cull);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, s.unpackAlign);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, s.unpackFlip);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, s.unpackPremul);
  // known values (see above)
  gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.blendEquation(gl.FUNC_ADD);
  gl.colorMask(true, true, true, true);
  gl.clearColor(0, 0, 0, 0);
  gl.scissor(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
}

/**
 * The widest gap (device px) between two cores that the line composite closes into one band (L1-15).
 * Only where the mood draws a contact shade: two lines whose shades touch (core gap ≤ shade + 1 CSS px)
 * would otherwise read as a pair of rails with a dark band between them. 0 when there is no shade.
 */
export function bridgePx(p: Pick<FrameParams, "shadePx" | "shade" | "dpr">): number {
  if (!(p.shadePx > 0) || !(p.shade[3] > 0)) return 0;
  return Math.min(BRIDGE_MAX_PX, Math.ceil((p.shadePx + 1) * Math.max(1, p.dpr)));
}

/* ─── Renderer ──────────────────────────────────────────────────────────── */

export class LightRenderer {
  readonly gl: WebGL2RenderingContext;
  private line: Prog;
  private glowP: Prog;
  private compLine: Prog;
  private compGlow: Prog;
  private cometP: Prog;
  private vaoLine: WebGLVertexArrayObject;
  private vaoComet: WebGLVertexArrayObject;
  private vaoEmpty: WebGLVertexArrayObject;
  private instBuf: WebGLBuffer;
  private cometBuf: WebGLBuffer;
  private cometCap = 0;
  private stateTex: WebGLTexture;
  private stateRows = 0;
  private fboLine: WebGLFramebuffer | null = null;
  private fboGlow: WebGLFramebuffer | null = null;
  private texCore: WebGLTexture | null = null;
  private texShade: WebGLTexture | null = null;
  private texGlow: WebGLTexture | null = null;
  private fw = 0;
  private fh = 0;
  private gw = 0;
  private gh = 0;
  instanceCount = 0;
  private disposed = false;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    if (gl.isContextLost()) throw new LightGLError("context lost");
    this.line = program(gl, SHADERS.lineVS, SHADERS.lineFS, "line", [...CAPSULE_UNIFORMS, "u_color", "u_trail", "u_comet"]);
    this.glowP = program(gl, SHADERS.glowVS, SHADERS.glowFS, "glow", [...CAPSULE_UNIFORMS, "u_glow"]);
    this.compLine = program(gl, SHADERS.fullscreenVS, SHADERS.compositeLineFS, "compositeLine", ["u_core", "u_shadeTex", "u_shade", "u_bridge"]);
    this.compGlow = program(gl, SHADERS.fullscreenVS, SHADERS.compositeGlowFS, "compositeGlow", ["u_glowTex", "u_full", "u_gain"]);
    this.cometP = program(gl, SHADERS.cometVS, SHADERS.cometFS, "comet", ["u_matrix", "u_viewport", "u_dpr", "u_near", "u_cometColor", "u_halo"]);

    const prevVao = gl.getParameter(gl.VERTEX_ARRAY_BINDING) as WebGLVertexArrayObject | null;
    const prevBuf = gl.getParameter(gl.ARRAY_BUFFER_BINDING) as WebGLBuffer | null;
    const prevTex = gl.getParameter(gl.TEXTURE_BINDING_2D) as WebGLTexture | null;

    this.instBuf = gl.createBuffer()!;
    this.cometBuf = gl.createBuffer()!;
    this.vaoLine = gl.createVertexArray()!;
    this.vaoComet = gl.createVertexArray()!;
    this.vaoEmpty = gl.createVertexArray()!;

    gl.bindVertexArray(this.vaoLine);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);
    const attr = (loc: number, size: number, offset: number, stride: number) => {
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride, offset);
      gl.vertexAttribDivisor(loc, 1);
    };
    attr(0, 2, 0, INSTANCE_BYTES);
    attr(1, 2, 8, INSTANCE_BYTES);
    attr(2, 2, 16, INSTANCE_BYTES);
    attr(3, 1, 24, INSTANCE_BYTES);

    gl.bindVertexArray(this.vaoComet);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.cometBuf);
    attr(0, 2, 0, COMET_FLOATS * 4);
    attr(1, 4, 8, COMET_FLOATS * 4);

    gl.bindVertexArray(prevVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, prevBuf);

    this.stateTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.stateTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, prevTex);
  }

  /** Upload the static instance buffer (all paths, packed once). */
  setInstances(data: Float32Array, count: number): void {
    const gl = this.gl;
    const prev = gl.getParameter(gl.ARRAY_BUFFER_BINDING) as WebGLBuffer | null;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);
    gl.bufferData(gl.ARRAY_BUFFER, data.byteLength ? data : new Float32Array(8), gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, prev);
    this.instanceCount = count;
  }

  private ensureTargets(w: number, h: number): void {
    if (w === this.fw && h === this.fh && this.fboLine && this.fboGlow) return;
    const gl = this.gl;
    this.freeTargets();
    const mk = (iw: number, ih: number, internal: number, filter: number): WebGLTexture => {
      const t = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texStorage2D(gl.TEXTURE_2D, 1, internal, iw, ih);
      return t;
    };
    this.fw = w;
    this.fh = h;
    this.gw = Math.max(1, Math.ceil(w / 2));
    this.gh = Math.max(1, Math.ceil(h / 2));
    this.texCore = mk(w, h, gl.RGBA8, gl.NEAREST);
    this.texShade = mk(w, h, gl.R8, gl.NEAREST);
    this.texGlow = mk(this.gw, this.gh, gl.RGBA8, gl.LINEAR);

    this.fboLine = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboLine);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.texCore, 0);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, this.texShade, 0);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
    const okLine = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;

    this.fboGlow = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboGlow);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.texGlow, 0);
    const okGlow = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    if (!okLine || !okGlow) {
      // Never keep half-built targets: the next frame would skip the size check and draw into them.
      this.freeTargets();
      throw new LightGLError("offscreen framebuffer incomplete");
    }
  }

  private freeTargets(): void {
    const gl = this.gl;
    gl.deleteFramebuffer(this.fboLine);
    gl.deleteFramebuffer(this.fboGlow);
    gl.deleteTexture(this.texCore);
    gl.deleteTexture(this.texShade);
    gl.deleteTexture(this.texGlow);
    this.fboLine = this.fboGlow = null;
    this.texCore = this.texShade = this.texGlow = null;
    this.fw = this.fh = 0;
  }

  /** Offscreen passes (call from CustomLayerInterface.prerender). `state` is uploaded first when given. */
  prerender(p: FrameParams, state: Float32Array | null, rows: number, comets: Float32Array | null): void {
    const gl = this.gl;
    if (this.disposed || gl.isContextLost()) return;
    const s = snapshot(gl);
    try {
      this.uploadState(state, rows);
      this.uploadComets(comets, p.comets);
      if (!this.instanceCount || !this.stateRows) return;
      gl.activeTexture(gl.TEXTURE0); // targets are (re)created on a unit the snapshot restores
      this.ensureTargets(p.width, p.height);
      if (!p.lineRanges.length) return; // comets only: render() draws them without the targets

      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.STENCIL_TEST);
      gl.disable(gl.CULL_FACE);
      gl.colorMask(true, true, true, true);
      gl.clearColor(0, 0, 0, 0);
      gl.enable(gl.BLEND);
      gl.blendEquation(gl.MAX);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, this.stateTex);
      gl.bindVertexArray(this.vaoLine);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);

      if (p.glow && p.glowRanges.length) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboGlow);
        gl.viewport(0, 0, this.gw, this.gh);
        this.scissor(p.scissor, this.gw / this.fw, this.gh / this.fh, this.gw, this.gh);
        gl.clear(gl.COLOR_BUFFER_BIT);
        const pr = this.glowP;
        gl.useProgram(pr.p);
        // Capsules are built in FULL-resolution device px; the glow viewport maps the same NDC onto
        // gw × gh texels. (A 2·gw × 2·gh "virtual" frame misregistered the glow by up to 1 px toward
        // the right/top edge whenever the drawing buffer had an odd width or height.)
        this.capsuleUniforms(pr, p, this.fw, this.fh);
        gl.uniform3fv(pr.u.u_glow, p.glowColor);
        this.drawRanges(p.glowRanges);
      }

      gl.bindFramebuffer(gl.FRAMEBUFFER, this.fboLine);
      gl.viewport(0, 0, this.fw, this.fh);
      this.scissor(p.scissor, 1, 1, this.fw, this.fh);
      gl.clear(gl.COLOR_BUFFER_BIT);
      const pr = this.line;
      gl.useProgram(pr.p);
      this.capsuleUniforms(pr, p, this.fw, this.fh);
      gl.uniform3fv(pr.u.u_color, p.colors);
      gl.uniform3fv(pr.u.u_trail, p.trail);
      gl.uniform3fv(pr.u.u_comet, p.comet);
      this.drawRanges(p.lineRanges);
    } finally {
      restore(gl, s);
    }
  }

  /** Composites + comets into the bound (map) framebuffer (call from CustomLayerInterface.render). */
  render(p: FrameParams): void {
    const gl = this.gl;
    if (this.disposed || gl.isContextLost()) return;
    // The composites read targets written by prerender() this frame; comets need none.
    const lines = p.lineRanges.length > 0 && !!this.fboLine && p.width === this.fw && p.height === this.fh;
    if (!lines && !p.comets) return;
    const s = snapshot(gl);
    try {
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.STENCIL_TEST);
      gl.disable(gl.CULL_FACE);
      gl.colorMask(true, true, true, true);
      gl.enable(gl.BLEND);
      gl.blendEquation(gl.FUNC_ADD);
      gl.viewport(0, 0, p.width, p.height);
      this.scissor(p.scissor, 1, 1, p.width, p.height);
      gl.bindVertexArray(this.vaoEmpty);

      if (lines && p.glow && p.glowRanges.length && p.glowGain > 0) {
        gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_COLOR, gl.ZERO, gl.ONE); // screen
        const pr = this.compGlow;
        gl.useProgram(pr.p);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.texGlow);
        gl.uniform1i(pr.u.u_glowTex, 0);
        gl.uniform2f(pr.u.u_full, this.fw, this.fh); // uv = fragment / full-res size (same NDC as the capsules)
        gl.uniform1f(pr.u.u_gain, p.glowGain);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }

      gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      if (lines) {
        const pr = this.compLine;
        gl.useProgram(pr.p);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.texCore);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, this.texShade);
        gl.uniform1i(pr.u.u_core, 0);
        gl.uniform1i(pr.u.u_shadeTex, 1);
        gl.uniform4f(pr.u.u_shade, p.shade[0], p.shade[1], p.shade[2], p.shadePx > 0 ? p.shade[3] : 0);
        gl.uniform1i(pr.u.u_bridge, bridgePx(p));
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }

      if (p.comets > 0) {
        const pr = this.cometP;
        gl.useProgram(pr.p);
        gl.bindVertexArray(this.vaoComet);
        gl.uniformMatrix4fv(pr.u.u_matrix, false, p.matrix);
        gl.uniform2f(pr.u.u_viewport, p.width, p.height);
        gl.uniform1f(pr.u.u_dpr, p.dpr);
        gl.uniform1f(pr.u.u_near, p.near);
        gl.uniform3fv(pr.u.u_cometColor, p.comet);
        gl.uniform3fv(pr.u.u_halo, p.halo);
        gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, p.comets);
      }
    } finally {
      restore(gl, s);
    }
  }

  private capsuleUniforms(pr: Prog, p: FrameParams, vw: number, vh: number): void {
    const gl = this.gl;
    gl.uniformMatrix4fv(pr.u.u_matrix, false, p.matrix);
    gl.uniform2f(pr.u.u_viewport, vw, vh);
    gl.uniform1f(pr.u.u_dpr, p.dpr);
    gl.uniform1f(pr.u.u_near, p.near);
    gl.uniform1f(pr.u.u_shadePx, p.shadePx);
    gl.uniform1f(pr.u.u_glowSigma, GLOW_SIGMA_PER_WIDTH);
    gl.uniform1f(pr.u.u_glowMin, GLOW_SIGMA_MIN_PX);
    gl.uniform1f(pr.u.u_trailM, p.trailM);
    gl.uniform1i(pr.u.u_state, 2);
  }

  /** Draw the capsule strip for each [first, count] range (vaoLine + instBuf bound): re-points the instance attributes. */
  private drawRanges(ranges: readonly number[]): void {
    const gl = this.gl;
    for (let i = 0; i + 1 < ranges.length; i += 2) {
      const first = ranges[i], count = ranges[i + 1];
      if (count <= 0 || first < 0 || first + count > this.instanceCount) continue;
      const base = first * INSTANCE_BYTES;
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, INSTANCE_BYTES, base);
      gl.vertexAttribPointer(1, 2, gl.FLOAT, false, INSTANCE_BYTES, base + 8);
      gl.vertexAttribPointer(2, 2, gl.FLOAT, false, INSTANCE_BYTES, base + 16);
      gl.vertexAttribPointer(3, 1, gl.FLOAT, false, INSTANCE_BYTES, base + 24);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
    }
  }

  /** Scissor to `box` (full-res device px) scaled by kx/ky into a w × h target, 1 px padding; null = off. */
  private scissor(box: FrameParams["scissor"], kx: number, ky: number, w: number, h: number): void {
    const gl = this.gl;
    if (!box) {
      gl.disable(gl.SCISSOR_TEST);
      return;
    }
    const x0 = Math.max(0, Math.floor(box[0] * kx) - 1);
    const y0 = Math.max(0, Math.floor(box[1] * ky) - 1);
    const x1 = Math.min(w, Math.ceil((box[0] + box[2]) * kx) + 1);
    const y1 = Math.min(h, Math.ceil((box[1] + box[3]) * ky) + 1);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(x0, y0, Math.max(0, x1 - x0), Math.max(0, y1 - y0));
  }

  private uploadState(data: Float32Array | null, rows: number): void {
    if (!data) return;
    const gl = this.gl;
    gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, null);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.stateTex);
    const n = Math.max(1, rows);
    const src = data.length >= n * STATE_FLOATS ? data.subarray(0, n * STATE_FLOATS) : new Float32Array(n * STATE_FLOATS);
    if (n !== this.stateRows) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, STATE_TEXELS, n, 0, gl.RGBA, gl.FLOAT, src);
      this.stateRows = rows > 0 ? n : 0;
    } else {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, STATE_TEXELS, n, gl.RGBA, gl.FLOAT, src);
    }
  }

  private uploadComets(data: Float32Array | null, count: number): void {
    if (!data || !count) return;
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.cometBuf);
    const bytes = count * COMET_FLOATS * 4;
    if (bytes > this.cometCap) {
      this.cometCap = Math.max(bytes, 64 * COMET_FLOATS * 4);
      gl.bufferData(gl.ARRAY_BUFFER, this.cometCap, gl.DYNAMIC_DRAW);
    }
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, count * COMET_FLOATS);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const gl = this.gl;
    this.freeTargets();
    for (const pr of [this.line, this.glowP, this.compLine, this.compGlow, this.cometP]) gl.deleteProgram(pr.p);
    gl.deleteVertexArray(this.vaoLine);
    gl.deleteVertexArray(this.vaoComet);
    gl.deleteVertexArray(this.vaoEmpty);
    gl.deleteBuffer(this.instBuf);
    gl.deleteBuffer(this.cometBuf);
    gl.deleteTexture(this.stateTex);
  }
}
